// Serviço para gerenciar imagens localmente no frontend
class LocalImageService {
  constructor() {
    this.images = new Map(); // Armazena imagens em memória
    this.imageUrls = new Map(); // Armazena URLs blob das imagens
  }

  // Processa e armazena uma imagem localmente
  async storeImage(file) {
    try {
      // Gera um ID único para a imagem
      const imageId = this.generateImageId();
      
      // Cria URL blob para a imagem
      const imageUrl = URL.createObjectURL(file);
      
      // Processa a imagem (redimensiona se necessário)
      const processedImageData = await this.processImage(file);
      
      // Armazena a imagem
      const imageData = {
        id: imageId,
        originalName: file.name,
        filename: `${imageId}.${this.getFileExtension(file.name)}`,
        url: imageUrl,
        size: file.size,
        mimeType: file.type,
        ...processedImageData
      };
      
      this.images.set(imageId, imageData);
      this.imageUrls.set(imageId, imageUrl);
      
      return {
        success: true,
        data: {
          images: [imageData]
        }
      };
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      return {
        success: false,
        message: 'Erro ao processar imagem'
      };
    }
  }

  // Processa a imagem (redimensiona, otimiza)
  async processImage(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Define dimensões máximas
        const maxWidth = 1200;
        const maxHeight = 1600;
        
        let { width, height } = img;
        
        // Redimensiona se necessário
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Desenha a imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        
        resolve({
          width: Math.round(width),
          height: Math.round(height)
        });
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  // Gera ID único para a imagem
  generateImageId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${random}`;
  }

  // Extrai extensão do arquivo
  getFileExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  }

  // Recupera uma imagem armazenada
  getImage(imageId) {
    return this.images.get(imageId);
  }

  // Recupera URL da imagem
  getImageUrl(imageId) {
    return this.imageUrls.get(imageId);
  }

  // Lista todas as imagens armazenadas
  getAllImages() {
    return Array.from(this.images.values());
  }

  // Remove uma imagem
  removeImage(imageId) {
    const imageUrl = this.imageUrls.get(imageId);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    
    this.images.delete(imageId);
    this.imageUrls.delete(imageId);
  }

  // Limpa todas as imagens (útil para cleanup)
  clearAll() {
    // Revoga todas as URLs blob para liberar memória
    this.imageUrls.forEach(url => URL.revokeObjectURL(url));
    
    this.images.clear();
    this.imageUrls.clear();
  }

  // Converte imagem para dados que podem ser enviados ao backend
  getImageMetadata(imageId) {
    const image = this.images.get(imageId);
    if (!image) return null;
    
    return {
      id: image.id,
      originalName: image.originalName,
      filename: image.filename,
      size: image.size,
      width: image.width,
      height: image.height,
      mimeType: image.mimeType
    };
  }
}

// Instância singleton
const localImageService = new LocalImageService();

export default localImageService;